/**
 * The checkout state machine (H3).
 *
 * One donation, walked from creation to result. Each step is a real call and the
 * order is not negotiable:
 *
 *   1. `POST /api/give/donations` — the server plans the money and freezes it.
 *      The browser sends a **chosen amount in cents and nothing else**: no
 *      gross, no fee, no net, no seat count, no currency, no plan.
 *   2. `capture-context` — the signed context for *that* donation.
 *   3. Unified Checkout — card entry, producing a transient token.
 *   4. `auth-setup` — the server holds the Payer Auth reference.
 *   5. Device data collection in a hidden frame.
 *   6. `auth-enroll` — frictionless, or a challenge to show.
 *   7. `pay` — one capture, guarded so a double click cannot charge twice.
 *   8. Result: success with a receipt number, pending (202) with no retry, or a
 *      decline that offers a **new attempt**.
 *
 * The adapter is a parameter, not an import: that is the only thing a test
 * substitutes. Everything else — the client, the trust rules — is the real one.
 */

import {
  type AuthSetupResult,
  type CreateDonationInput,
  collectBrowserFields,
  type PaymentClient,
} from './api';
import type { DeviceDataGate } from './DdcFrame';
import { debugStage, debugStart, debugTimer } from './debugLog';
import type { CaptureContextResponse, UnifiedCheckoutAdapter } from './uc';
import { UnifiedCheckoutError } from './uc';

/**
 * How long to wait for the device-data-collection frame before enrolling anyway.
 *
 * Device data improves the fraud signal; it does not decide whether the payment
 * is allowed. A slow or blocked collector must not turn a good card into a
 * checkout that hangs, so the flow proceeds regardless after this.
 */
export const DDC_TIMEOUT_MS = 5_000;

/**
 * How long the challenge may stay silent before the page stops waiting.
 *
 * The challenge frame is where a checkout can hang forever: the issuer's page
 * never loads, the donor abandons it halfway, or the completion message is
 * lost — and the parent has no way to tell "still typing the one-time code"
 * from "nobody will ever speak again". Before this watchdog existed, a failed
 * validation on the server left the donor staring at a loading state that had
 * no end, because the failure redirect raced a frame that never delivered it.
 *
 * Forty-five seconds covers the slowest honest OTP entry with room to spare;
 * the setting (`VITE_3DS_WATCHDOG_MS`) exists so a box can tune it without a
 * rebuild. The clock starts when the challenge frame is created — that is the
 * moment the ACS was asked — and any trusted message wins the race, because the
 * timer only ever fires into a stage the message has already left.
 */
export const CHALLENGE_WATCHDOG_DEFAULT_MS = 45_000;

/** The watchdog length in effect, from the setting or the default. */
export function challengeWatchdogMs(): number {
  const raw = import.meta.env.VITE_3DS_WATCHDOG_MS;
  const parsed = typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : CHALLENGE_WATCHDOG_DEFAULT_MS;
}

/**
 * How long any waiting state may last before the page says so.
 *
 * "Waiting" covers every stage where the donor has done their part and the
 * page has not answered: creating, card entry, authentication, challenge, pay.
 * The owner's live stall sat inside one for as long as anybody watched, and a
 * spinner with no words is indistinguishable from a dead page - so after this
 * long the page shows a sentence and a way to copy the flight recorder's
 * facts. Configurable with `VITE_STILL_WAITING_MS`; the default is short
 * enough to feel responsive and long enough to stay quiet on a slow phone.
 */
export const STILL_WAITING_DEFAULT_MS = 20_000;

/** The still-waiting interval in effect, from the setting or the default. */
export function stillWaitingMs(): number {
  const raw = import.meta.env.VITE_STILL_WAITING_MS;
  const parsed = typeof raw === 'string' ? Number(raw) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : STILL_WAITING_DEFAULT_MS;
}

export type CheckoutStage =
  | 'idle'
  | 'creating'
  | 'card'
  | 'authenticating'
  | 'challenging'
  | 'paying'
  | 'success'
  | 'pending'
  | 'declined'
  /**
   * The automatic policy released the authorization: no money was taken, and
   * nothing was wrong with the donor's card.
   *
   * Its own stage, because the two are not the same event to a donor. A decline
   * invites trying another card; this one invites not trying this one again.
   */
  | 'declinedByPolicy'
  | 'failed';

export interface CheckoutState {
  stage: CheckoutStage;
  donationId?: string;
  /** Held in memory only, never rendered and never logged. */
  guestToken?: string;
  transientToken?: string;
  seats?: number;
  receiptNumber?: string;
  /** A code the UI maps to a message; never a raw server string. */
  errorCode?: string;
  /** Donor-facing text for a library failure. Never a server body. */
  errorDetail?: string;
  /**
   * True when the gateway answered `payment_in_review`.
   *
   * Decision Manager has the order and a person will accept or reject it. The
   * donor is told so explicitly, because "we are checking" and "somebody is
   * deciding whether to take your money" are different sentences.
   */
  inReview?: boolean;
  /**
   * True when the automatic policy released this order: `declined_by_risk_policy`.
   *
   * The donor is told plainly that nothing was taken and that their card is not
   * the problem, then offered another card or a bank transfer. No retry button
   * for the same card, because the policy has already answered for it.
   */
  declinedByPolicy?: boolean;
  stepUpUrl?: string;
  accessToken?: string;
  /** The DDC origin the server derived, carried into the DDC frame. */
  ddcOrigin?: string;
  deviceDataCollectionUrl?: string;
}

export const INITIAL_STATE: CheckoutState = { stage: 'idle' };

export interface CheckoutDeps {
  client: PaymentClient;
  adapter: UnifiedCheckoutAdapter;
  /** Injected so a test does not have to invent a user agent. */
  browserFields?: () => Record<string, unknown>;
  /** The hand-off to the device-data-collection frame. */
  deviceData?: DeviceDataGate;
}

export interface StartInput {
  chosenAmountCents: number;
  coverFees: boolean;
  method: 'CARD' | 'BANK_TRANSFER';
  hideName?: boolean;
  guestEmail?: string;
  /**
   * The campaign the gift is for, by slug.
   *
   * The slug comes from the link the donor followed, and the server resolves
   * it: a slug that names no open campaign is refused there, not here.
   */
  campaignSlug?: string;
  /** Where the donor came from. */
  source?: string;
  /** The marketing parameters the link carried. */
  utm?: Record<string, string>;
}

/**
 * A creation key that is stable for one checkout attempt and different for the
 * next one.
 *
 * The key is what makes a double click one donation rather than two, so it is
 * minted once per {@link run} entry — not per render, and not per step.
 */
function newIdempotencyKey(): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.trunc(Math.random() * 1e9)}`;
  return `web-checkout-${random}`;
}

/** The guest token a previous run produced, so a retry reuses the same donation. */
export type RunResult = CheckoutState & { donationId?: string };

/**
 * Run the card flow to completion.
 *
 * Returns the state the donor ends in. It never throws: a failure becomes
 * `stage: 'failed'` with a code, because a rejected promise escaping into a
 * click handler is how a real payment becomes a dead button.
 */
export async function run(
  deps: CheckoutDeps,
  input: StartInput,
  onState: (state: CheckoutState) => void,
  previous?: RunResult,
): Promise<CheckoutState> {
  const emit = (state: CheckoutState): CheckoutState => {
    // The flight recorder's spine: every state change is one timestamped line,
    // which is what turns "it stopped after the card frame" into "it stopped
    // between `card` and `authenticating`, 3.1s in".
    debugStage(state.stage);
    onState(state);
    return state;
  };
  const browserFields = deps.browserFields ?? collectBrowserFields;

  let state: CheckoutState = { ...INITIAL_STATE };

  try {
    debugStart();
    // ---- 1. Create the donation -----------------------------------------
    state = emit({ ...state, stage: 'creating' });

    // A retry after a decline re-uses the donation it already created: the
    // amount, the fee and the seat count are frozen on that row, and re-running
    // `pay` on it is what `auth-setup` turns into a fresh attempt. Creating a
    // second donation would strand the first as PENDING forever.
    let donationId = previous?.donationId;
    let guestToken = previous?.guestToken;
    if (!donationId) {
      const body: CreateDonationInput = {
        chosenAmountCents: input.chosenAmountCents,
        method: 'CARD',
        coverFees: input.coverFees,
        hideName: input.hideName ?? false,
        idempotencyKey: newIdempotencyKey(),
        ...(input.guestEmail ? { guestEmail: input.guestEmail } : {}),
        ...(input.campaignSlug ? { campaignSlug: input.campaignSlug } : {}),
        ...(input.source ? { source: input.source } : {}),
        ...(input.utm ? { utm: input.utm } : {}),
      };
      const created = await deps.client.createDonation(body);
      donationId = created.donationId;
      guestToken = created.guestToken ?? guestToken;
    }
    if (!donationId) throw new CheckoutError('donation_not_created');

    state = emit({ ...state, donationId, guestToken });

    // ---- 2 & 3. Capture context, then card entry ------------------------
    state = emit({ ...state, stage: 'card' });
    const capture: CaptureContextResponse = await deps.client.captureContext(
      donationId,
      guestToken,
    );
    const transientToken = await deps.adapter.accept(capture);
    state = emit({ ...state, transientToken });

    // ---- 4. Payer Auth setup --------------------------------------------
    state = emit({ ...state, stage: 'authenticating' });
    const setup: AuthSetupResult = await deps.client.authSetup(
      donationId,
      transientToken,
      guestToken,
    );
    // Device data collection (step 5) needs the URL Payer Auth Setup returned
    // and the origin the **server** derived from it. Both are carried on the
    // state so the page can mount the hidden frame; a hardcoded Cardinal/3DS
    // host here is exactly what 06 §3.8 forbids.
    state = emit({
      ...state,
      accessToken: setup.accessToken,
      deviceDataCollectionUrl: setup.deviceDataCollectionUrl,
      ddcOrigin: setup.ddcOrigin,
    });

    // ---- 6. Payer Auth enroll -------------------------------------------
    // Device data collection (step 5) is given its chance first: the frame the
    // page just mounted posts back when Cardinal has what it needs, and a slow
    // collector must not hold the payment hostage. After `DDC_TIMEOUT_MS` the
    // flow enrolls anyway, because device data improves the fraud signal and
    // does not decide whether the payment is allowed.
    if (deps.deviceData) {
      debugTimer('ddc', 'started');
      await deps.deviceData.wait(DDC_TIMEOUT_MS);
      debugTimer('ddc', 'fired');
    }

    const enroll = await deps.client.authEnroll(
      donationId,
      transientToken,
      browserFields(),
      guestToken,
    );

    if (!enroll.frictionless) {
      if (!enroll.stepUpUrl) throw new CheckoutError('challenge_not_ready');
      state = emit({
        ...state,
        stage: 'challenging',
        stepUpUrl: enroll.stepUpUrl,
        accessToken: enroll.accessToken,
      });
      // `run` stops here. The challenge component drives the rest: on
      // `3ds-complete` the caller calls {@link pay}.
      return state;
    }

    state = emit({ ...state, stage: 'authenticating' });
    return await pay(deps, state, onState);
  } catch (error) {
    return emit(failure(state, error));
  }
}

/**
 * Capture the donation, once 3-D Secure has been satisfied (or skipped).
 *
 * Separate from {@link run} because the challenge completes through a
 * `postMessage` long after `run` returned, and that message is the only thing
 * allowed to call this.
 */
export async function pay(
  deps: CheckoutDeps,
  state: CheckoutState,
  onState: (state: CheckoutState) => void,
): Promise<CheckoutState> {
  const emit = (s: CheckoutState): CheckoutState => {
    onState(s);
    return s;
  };
  const { donationId, transientToken, guestToken } = state;
  if (!donationId || !transientToken) {
    return emit(failure(state, new CheckoutError('payment_not_ready')));
  }

  try {
    const result = await deps.client.pay(donationId, transientToken, guestToken);
    if (result.pending) {
      // 202: either the gateway's answer is unknown, or Decision Manager has
      // taken the order for review. Reconciliation owns the first; a person owns
      // the second. Offering a retry here would be the dangerous choice in both
      // cases — the money may already have moved, and a second capture would
      // charge twice.
      return emit({
        ...state,
        stage: 'pending',
        seats: result.seats,
        ...(result.inReview ? { inReview: true } : {}),
      });
    }
    if (result.declined) {
      // A release by the automatic policy is a different thing to a card
      // decline, and it gets its own stage: the donor's card is fine, the order
      // was not, and asking them to try the same card again would be asking the
      // question that was just answered.
      if (result.declinedByPolicy) {
        return emit({ ...state, stage: 'declinedByPolicy', declinedByPolicy: true });
      }
      return emit({ ...state, stage: 'declined' });
    }
    // The thank-you screen is earned, never defaulted to. It needs **an applied
    // credit with a receipt number** — both, because the live box once answered
    // a 202 inside a 2xx envelope and the fall-through below showed the success
    // screen with "Seats funded: 0" for a payment that had not been credited.
    // A 200 that does not carry both is a payment the server did not apply, and
    // it gets the failure screen, never a celebration.
    if (
      result.applied === true &&
      typeof result.receiptNumber === 'string' &&
      result.receiptNumber.length > 0
    ) {
      return emit({
        ...state,
        stage: 'success',
        seats: result.seats,
        receiptNumber: result.receiptNumber,
      });
    }
    return emit({ ...state, stage: 'failed', errorCode: 'payment_unresolved' });
  } catch (error) {
    return emit(failure(state, error));
  }
}

/** A failure the flow can describe without inventing text. */
export class CheckoutError extends Error {
  override name = 'CheckoutError';
}

function failure(state: CheckoutState, error: unknown): CheckoutState {
  return {
    ...state,
    stage: 'failed',
    // A code, never a server message: a 5xx body can carry anything, and the
    // donor's screen must not echo it.
    errorCode: errorCodeFor(error),
    ...(error instanceof UnifiedCheckoutError ? { errorDetail: error.detail } : {}),
  };
}

/**
 * The code the page renders a message for.
 *
 * A `UnifiedCheckoutError` already carries one of a closed set, which is what
 * lets the donor be told *why* the form is missing instead of being handed a
 * generic apology. Anything else keeps the old behaviour.
 */
function errorCodeFor(error: unknown): string {
  if (error instanceof UnifiedCheckoutError) return error.code;
  return error instanceof Error ? error.name : 'unknown';
}
